import { Checkout } from '@seek/hirer-ecommerce';
import {
  getSupportedPaymentMethodsForCaja,
  type PaymentMethodType,
} from '@seek/payments-widget';
import { useTranslations } from '@vocab/react';
import { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { JOB_PUBLISH_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useQueryParam } from 'src/app/shared/hooks/useQueryParam';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getProductCategories } from 'src/app/utils/analytics/utils/getProductCategories';
import { convertToCurrency } from 'src/app/utils/currency';
import { getEnvironment } from 'src/app/utils/environment';
import { isIgnoredError } from 'src/app/utils/isIgnoredCheckoutError';
import { log } from 'src/app/utils/logging';
import { PRODUCT_EVENTS_PURCHASE } from 'src/app/v1/analytics';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateProductCurrency } from 'src/app/v1/state/modules/session/productCurrency';
import { actionUpdateIsSaveCreditCardChecked } from 'src/app/v1/state/modules/tealium/isSaveCreditCardChecked';
import { actionUpdatePaymentMethod } from 'src/app/v1/state/modules/tealium/paymentMethod';
import { actionUpdateProductTotalPrice } from 'src/app/v1/state/modules/tealium/productTotalPrice';
import { useEditJob } from 'src/app/v1/views/hooks/useEditJob';
import { useGenerateJobIdempotencyKey } from 'src/app/v1/views/hooks/useGenerateJobIdempotencyKey';
import { PreviewButton } from 'src/app/v1/views/shared/PreviewButton';
import { useGetOrderSummaryTotalQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { CheckoutComponentCallbacks } from 'src/types/checkout';

import translations from '../../.vocab';

interface CheckoutSectionProps {
  onPaymentSuccess: CheckoutComponentCallbacks['onPaymentSuccess'];
  handleCancelledPayment: CheckoutComponentCallbacks['handleCancelledPayment'];
  resetError: () => void;
  setError: (error: Error | string) => void;
}

export const CheckoutSection = ({
  resetError,
  setError,
  onPaymentSuccess,
  handleCancelledPayment,
}: CheckoutSectionProps) => {
  const { t } = useTranslations(translations);
  const {
    advertiserNation,
    country,
    draftId,
    sourceJobId,
    productTotalPrice,
    adProductSetId,
    brandHasCoverImage,
    brandHasLogo,
    jobAdTypeSelected,
    jobIsLinkOut,
    productCategories,
    productSetProductIds,
    seekAdvertiserId,
  } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      country: state.tealium.country,
      draftId: state.session.draftId,
      sourceJobId: state.session.sourceJobId,
      productTotalPrice: state.tealium.productTotalPrice,
      adProductSetId: state.tealium.productSetId,
      brandHasCoverImage: state.tealium.hasCoverImage,
      brandHasLogo: state.tealium.hasLogo,
      jobAdTypeSelected: [state.tealium.jobAdTypeSelected],
      jobIsLinkOut: Boolean(state.tealium.isLinkOutApplyViaSeek),
      productCategories: getProductCategories(state.tealium.adBudgetPurchased),
      productSetProductIds: state.tealium.productSetProductIds,
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const { getSite } = useConfig();

  const idempotencyKey = useGenerateJobIdempotencyKey();
  const editJob = useEditJob();
  const {
    assignJobIdAndRevisionToParentProduct,
    orderState: { order: { orderId } = {} },
  } = useShoppingCart();
  const { data: dataOrderSummaryTotal } = useGetOrderSummaryTotalQuery({
    variables: {
      orderId: orderId || '',
      advertiserId: seekAdvertiserId,
      advertiserNation,
    },
    skip: !orderId,
  });

  const paymentsReturnValue = useQueryParam('payments_return');
  const isReturnedFrom3rdPayment = Boolean(paymentsReturnValue);

  const productPrice = dataOrderSummaryTotal?.orderSummary?.total.value;
  const productCurrency = dataOrderSummaryTotal?.orderSummary?.total.currency;

  useEffect(() => {
    dispatch(
      actionUpdateProductTotalPrice(
        productPrice ? convertToCurrency(productPrice).toString() : '0.00',
      ),
    );
    dispatch(actionUpdateProductCurrency(productCurrency));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productPrice, productCurrency]);

  useEffect(() => {
    const shouldEditJob = !isReturnedFrom3rdPayment;

    if (shouldEditJob) {
      editJob(idempotencyKey).then((response) => {
        if (response && sourceJobId) {
          assignJobIdAndRevisionToParentProduct({
            jobId: sourceJobId,
            revisionId: response,
          });
        }
      });
    }
  }, [
    editJob,
    assignJobIdAndRevisionToParentProduct,
    idempotencyKey,
    isReturnedFrom3rdPayment,
    sourceJobId,
  ]);

  const basicLog = {
    draftId,
    jobId: sourceJobId,
  };

  const onPaymentFailed = (error: Error) => {
    if (isIgnoredError(error)) return;

    log.error(
      'CheckoutSection | onPaymentFailed | Payment failed with unexpected error',
      { error, sourceJobId },
    );

    setError(error);
  };

  const onTrackingEvent: CheckoutComponentCallbacks['onTrackingEvent'] = (
    payload,
  ) => {
    if (payload.tracking) {
      getTrackingAdapter.sendLinkEvent(payload.tracking);
    }
  };

  const onPostAdButtonClickFailed: CheckoutComponentCallbacks['onPostAdButtonClickFailed'] =
    (error) => {
      if (isIgnoredError(error)) {
        return;
      }

      log.error(
        'CheckoutSection | onPostAdButtonClickFailed | error posting ad',
        {
          error,
          ...basicLog,
        },
      );

      setError(error);
    };

  const onReplaceSavedCard: CheckoutComponentCallbacks['onReplaceSavedCard'] =
    () => dispatch(actionUpdatePaymentMethod('CreditCard'));

  const onSaveCardOptionChanged: CheckoutComponentCallbacks['onSaveCardOptionChanged'] =
    (shouldSave) => dispatch(actionUpdateIsSaveCreditCardChecked(shouldSave));

  const onSavedCardLoaded: CheckoutComponentCallbacks['onSavedCardLoaded'] =
    () => dispatch(actionUpdatePaymentMethod('SavedCreditCard'));

  const onPaymentOptionChanged: NonNullable<
    CheckoutComponentCallbacks['handlePaymentOptionChange']
  > = (_selectedIndex, selectedItem) => {
    if (!selectedItem) {
      return;
    }

    const paymentMethod = selectedItem as PaymentMethodType;
    dispatch(actionUpdatePaymentMethod(paymentMethod));
  };

  const onPostAdButtonClick = async () => {
    // TODO move this function call up to editpayandpost page level schema after rollout, similar to checkout page schema
    const paymentMethods = getSupportedPaymentMethodsForCaja(
      country.toUpperCase(),
    );

    const eventData = {
      eventName: JOB_PUBLISH_PRESSED,
      adStatus: 'open',
      paymentMethodShown: paymentMethods,
      productEvents: [PRODUCT_EVENTS_PURCHASE],
      productTotalPrice,
      adProductSetId,
      brandHasCoverImage,
      brandHasLogo,
      jobAdTypeSelected,
      jobIsLinkOut,
      productCategories,
      productSetProductIds,
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    resetError();
  };

  const notifyFatalError: CheckoutComponentCallbacks['notifyFatalError'] = (
    error,
  ) => {
    // NOTE: Checkout component itself renders an alert upon create_order_fatal_error
    if (error.reason === 'create_order_fatal_error') {
      log.error(
        'CheckoutSection | notifyFatalError | create_order_fatal_error',
        { error, ...basicLog },
      );

      return;
    }

    if (error.reason === 'product_unavailable') {
      log.error('CheckoutSection | notifyFatalError | product_unavailable', {
        error,
        ...basicLog,
      });
    }

    setError(new Error(error.reason));
  };

  return (
    <Checkout
      callbacks={{
        handleCancelledPayment,
        handlePaymentOptionChange: onPaymentOptionChanged,
        notifyFatalError,
        onPaymentFailed,
        onPaymentSuccess,
        onPostAdButtonClick,
        onPostAdButtonClickFailed,
        onReplaceSavedCard,
        onSaveCardOptionChanged,
        onSavedCardLoaded,
        onTrackingEvent,
      }}
      env={getEnvironment()}
      idempotencyKey={idempotencyKey}
      site={getSite()}
      useCase="CAJA"
      logger={log}
      previewButtonComponent={<PreviewButton />}
      postAdButtonText={t('edit-pay-post-page-button-label')}
    />
  );
};
